import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Eye, FileText, Image as ImageIcon, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';

/**
 * Zona de arrastrar/elegir archivo que NO sube nada: deja el `File` elegido
 * en manos del que llama. Para formularios de alta, donde la fila todavía no
 * existe y `FileUploader` (que sube al toque contra un entityId) no sirve:
 * se crea la fila y recién ahí se sube con `uploadDocument`.
 *
 * Mismos topes que `FileUploader` y que el server (`routes/documents.ts`):
 * PDF/PNG/JPG, 4 MB. La vista previa (ojo) usa el mismo diálogo que
 * `FileLink`, así un archivo se ve igual antes y después de subirlo.
 */
const MAX_BYTES = 4 * 1024 * 1024;
const TIPOS = ['application/pdf', 'image/png', 'image/jpeg'];

export function FilePicker({
  file,
  onChange,
  disabled,
  hint = 'Opcional',
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [viendo, setViendo] = useState(false);
  // URL local para la vista previa; se libera al cambiar o quitar el archivo.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const elegir = (files: FileList | null | undefined) => {
    const f = files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!f) return;
    if (!TIPOS.includes(f.type)) {
      toast.error('Tipo de archivo no soportado', { description: 'Se aceptan PDF o imagen (PNG/JPG).' });
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error('Archivo demasiado grande', {
        description: `Pesa ${(f.size / (1024 * 1024)).toFixed(1)} MB y el tope es 4 MB.`,
      });
      return;
    }
    onChange(f);
  };

  if (file) {
    const esPdf = file.type === 'application/pdf';
    const Icono = esPdf ? FileText : ImageIcon;
    return (
      <>
        <div className="flex min-h-[56px] items-center gap-3 rounded-xl border bg-card/40 px-4 py-2">
          <Icono className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground" title={file.name}>
              {file.name}
            </p>
            <p className="text-xs text-muted-foreground">
              {esPdf ? 'PDF' : 'Imagen'} · {(file.size / 1024).toFixed(0)} KB · se adjunta al guardar
            </p>
          </div>
          <button
            type="button"
            onClick={() => setViendo(true)}
            disabled={!url}
            aria-label="Ver archivo"
            title="Ver archivo"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label="Quitar archivo"
            title="Quitar archivo"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {viendo && url && (
          <Dialog open onOpenChange={(o) => !o && setViendo(false)}>
            <DialogContent className="max-w-5xl">
              <DialogHeader>
                <DialogTitle className="truncate">{file.name}</DialogTitle>
                <DialogDescription className="sr-only">Vista previa del archivo a adjuntar.</DialogDescription>
              </DialogHeader>
              <DialogBody className="p-0">
                {esPdf ? (
                  <iframe src={url} title={file.name} className="h-[70vh] w-full border-0" />
                ) : (
                  <img src={url} alt={file.name} className="max-h-[70vh] w-full object-contain" />
                )}
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.open(url, '_blank')}
                  className="gap-2"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  Abrir en pestaña nueva
                </Button>
                <Button type="button" onClick={() => setViendo(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  return (
    <div
      className={cn(
        'flex min-h-[56px] cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-2 text-center transition-colors',
        dragOver ? 'border-foreground bg-foreground/5' : 'border-muted-foreground/30 bg-card/40 hover:bg-card/70',
        disabled && 'pointer-events-none opacity-60',
      )}
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        elegir(e.dataTransfer?.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={TIPOS.join(',')}
        disabled={disabled}
        onChange={(e) => elegir(e.target.files)}
        className="hidden"
      />
      <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
      <div className="text-left">
        <p className="text-sm font-medium text-foreground">Arrastrá un archivo o hacé clic para elegirlo</p>
        <p className="text-xs text-muted-foreground">{hint} · PDF o imagen · máx. 4 MB</p>
      </div>
    </div>
  );
}
